import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Eye, FileText, Loader2, MoreVertical, Plus, Search, X } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  ClientMaterial,
  ClientMaterialsListResponse,
  MATERIAL_CATEGORY_LABEL,
  MATERIAL_SORT_LABEL,
  MaterialCategory,
  MaterialSort,
  deleteClientMaterial,
  fetchClientMaterials,
  formatFileSize,
  materialDownloadUrl,
  materialViewUrl,
} from "@/api/clientMaterials";
import { downloadAttachment, openAttachmentInNewTab } from "@/api/taskAttachments";
import { MaterialUploadModal } from "@/components/materials/MaterialUploadModal";
import { MaterialEditModal } from "@/components/materials/MaterialEditModal";
import { AttachmentViewer } from "@/components/attachments/AttachmentViewer";
import { useClient360 } from "@/hooks/useClients";

/**
 * D-146 W2 — Client360 tab «Матеріали» (10th tab, holding-grain, mockup v0.4 LOCK).
 *
 * Everything gate-like is backend-scoped: the add button renders ONLY on
 * meta.canCreate; read-only users get the «Лише перегляд» callout; the
 * limit_reached state disables create; row actions follow item.allowedActions
 * (an empty overflow set → the ⋯ button is NOT rendered). Counts in chips are
 * backend category counts — stable across search/paging (TZ §10).
 *
 * Mobile (<768, D-124 breakpoint): search full-width, category/sort as
 * selects, title clamps to 2 lines (TZ §14).
 */

const CATEGORY_KEYS: MaterialCategory[] = ["kp", "matrix", "marketing", "other"];

/** File-type tile colors per mockup v0.4. */
const EXT_TILE: Record<string, string> = {
  pdf: "bg-red-600",
  xlsx: "bg-green-600",
  docx: "bg-blue-600",
  pptx: "bg-orange-500",
  jpg: "bg-violet-600",
  png: "bg-violet-600",
};

export function useMaterialsQueryKey(holdingId: string) {
  return ["clientMaterials", holdingId] as const;
}

export function ClientMaterialsTab() {
  const { holdingId } = useParams<{ holdingId: string }>();
  const id = holdingId ?? "";
  const qc = useQueryClient();

  const env = useClient360(id);
  const holdingName = env.data?.header?.displayName || env.data?.header?.name || "";

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<MaterialCategory | "">("");
  const [sort, setSort] = useState<MaterialSort>("newest");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<ClientMaterial | null>(null);
  const [editing, setEditing] = useState<ClientMaterial | null>(null);
  const [deleting, setDeleting] = useState<ClientMaterial | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  // Debounced search (TZ §8).
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const q = useQuery({
    queryKey: ["clientMaterials", id, { search, category, sort }],
    queryFn: ({ signal }) => fetchClientMaterials(id, { search, category, sort }, signal),
    enabled: !!id,
    staleTime: 30_000,
  });

  const meta = q.data?.meta;
  const items = q.data?.items ?? [];
  const hasFilters = search !== "" || category !== "";

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["clientMaterials", id] });
    void qc.invalidateQueries({ queryKey: ["client360", id] }); // tab badge
  };

  return (
    <div className="p-6 md:p-8">
      <MaterialsSurface
        holdingId={id}
        state={{
          isPending: q.isPending,
          isError: q.isError,
          refetch: () => void q.refetch(),
        }}
        items={items}
        meta={meta}
        filteredTotal={q.data?.filteredTotal ?? 0}
        search={searchInput}
        onSearch={setSearchInput}
        category={category}
        onCategory={setCategory}
        sort={sort}
        onSort={setSort}
        hasFilters={hasFilters}
        onResetFilters={() => {
          setSearchInput("");
          setSearch("");
          setCategory("");
        }}
        onChanged={invalidate}
        onAdd={() => setUploadOpen(true)}
        onView={(m) => setViewing(m)}
        onEdit={(m) => setEditing(m)}
        onDelete={(m) => {
          setDeleteError(null);
          setDeleting(m);
        }}
        focusId={focusId}
      />

      {viewing && (
        <AttachmentViewer
          downloadUrl={materialViewUrl(viewing.id)}
          filename={viewing.name}
          subtitle={viewing.originalFilename}
          mime={viewing.mimeType ?? undefined}
          onClose={() => setViewing(null)}
        />
      )}

      {editing && (
        <MaterialEditModal
          material={editing}
          onClose={() => setEditing(null)}
          onSaved={(item) => {
            setEditing(null);
            invalidate();
            setFocusId(item.id);
            setToast("Зміни збережено");
          }}
        />
      )}

      {deleting && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center sm:p-6"
          onClick={() => !deleteBusy && setDeleting(null)}
          aria-hidden
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Підтвердження видалення"
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded-t-2xl bg-background p-5 shadow-2xl sm:max-w-md sm:rounded-2xl"
          >
            <h2 className="text-base font-semibold">Видалити «{deleting.name}»?</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">Файл зникне з картки клієнта.</p>
            {deleteError && (
              <div role="alert" className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {deleteError}
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                disabled={deleteBusy}
                onClick={() => setDeleting(null)}
                className="rounded-lg border px-3.5 py-2 text-sm font-medium hover:bg-accent disabled:opacity-40"
              >
                Скасувати
              </button>
              <button
                type="button"
                disabled={deleteBusy}
                onClick={() => {
                  setDeleteBusy(true);
                  setDeleteError(null);
                  deleteClientMaterial(deleting.id)
                    .then(() => {
                      setDeleting(null);
                      invalidate();
                      setToast("Матеріал видалено");
                    })
                    .catch((e: unknown) =>
                      setDeleteError(e instanceof Error ? e.message : "Не вдалося видалити."),
                    )
                    .finally(() => setDeleteBusy(false));
                }}
                className="rounded-lg bg-destructive px-3.5 py-2 text-sm font-semibold text-destructive-foreground disabled:opacity-50"
              >
                Видалити
              </button>
            </div>
          </div>
        </div>
      )}

      {uploadOpen && (
        <MaterialUploadModal
          holdingId={id}
          holdingName={holdingName}
          onClose={() => setUploadOpen(false)}
          onSuccess={(item, opts) => {
            setUploadOpen(false);
            invalidate();
            setFocusId(item?.id ?? null);
            setToast(opts?.serverWonRace ? "Матеріал уже збережено" : "Матеріал додано");
          }}
          onOpenExisting={(existingId) => {
            setUploadOpen(false);
            setSearchInput("");
            setSearch("");
            setCategory("");
            setFocusId(existingId);
          }}
        />
      )}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-5 left-1/2 z-[70] -translate-x-1/2 rounded-lg border border-success bg-success px-4 py-2 text-sm font-semibold text-success-foreground shadow-lg"
        >
          {toast}
        </div>
      )}
    </div>
  );
}

export interface MaterialsSurfaceProps {
  holdingId: string;
  state: { isPending: boolean; isError: boolean; refetch: () => void };
  items: ClientMaterial[];
  meta: ClientMaterialsListResponse["meta"] | undefined;
  filteredTotal: number;
  search: string;
  onSearch: (v: string) => void;
  category: MaterialCategory | "";
  onCategory: (v: MaterialCategory | "") => void;
  sort: MaterialSort;
  onSort: (v: MaterialSort) => void;
  hasFilters: boolean;
  onResetFilters: () => void;
  onChanged: () => void;
  /** Slice-4 wires the upload modal; absent → the CTA is rendered disabled. */
  onAdd?: () => void;
  /** Slice-5 wires viewer + edit/delete row actions. */
  onView?: (m: ClientMaterial) => void;
  onEdit?: (m: ClientMaterial) => void;
  onDelete?: (m: ClientMaterial) => void;
  /** Focus target row id (upload success → focus the new row, TZ §8). */
  focusId?: string | null;
}

export function MaterialsSurface(props: MaterialsSurfaceProps) {
  const { state, items, meta, search, hasFilters } = props;

  const canCreate = meta?.canCreate ?? false;
  const reason = meta?.canCreateReason ?? null;
  const readOnly = !canCreate && reason === "forbidden";
  const limitReached = !canCreate && reason === "limit_reached";

  return (
    <div>
      {readOnly && meta && (
        <div className="mb-4 rounded-lg border bg-muted/40 px-3.5 py-2.5 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">Лише перегляд.</span>{" "}
          Ви бачите матеріали цього холдингу, але не можете додавати, перейменовувати або видаляти їх.
        </div>
      )}
      {limitReached && (
        <div
          role="status"
          className="mb-4 rounded-lg border border-warning-foreground/20 bg-warning px-3.5 py-2.5 text-sm text-warning-foreground"
        >
          <span className="font-semibold">Досягнуто ліміт матеріалів ({meta?.maxCount ?? 100}).</span>{" "}
          Перегляд і завантаження доступні. Новий матеріал можна додати після видалення непотрібного.
        </div>
      )}

      {/* Toolbar: search · sort · add (mobile: column, full-width) */}
      <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center">
        <label className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input
            value={search}
            onChange={(e) => props.onSearch(e.target.value)}
            placeholder="Пошук за назвою або імʼям файлу…"
            aria-label="Пошук матеріалів за назвою або оригінальним імʼям файлу"
            className="w-full rounded-lg border bg-card py-2 pl-9 pr-8 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
          {search && (
            <button
              type="button"
              aria-label="Очистити пошук"
              onClick={() => props.onSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </label>

        {/* Mobile: category select (chips are ≥768-only, TZ §14). */}
        <select
          value={props.category}
          onChange={(e) => props.onCategory(e.target.value as MaterialCategory | "")}
          aria-label="Фільтр за категорією"
          className="rounded-lg border bg-card px-3 py-2 text-sm md:hidden"
        >
          <option value="">Усі категорії{meta ? ` · ${meta.activeCount}` : ""}</option>
          {CATEGORY_KEYS.map((c) => (
            <option key={c} value={c}>
              {MATERIAL_CATEGORY_LABEL[c]}
              {meta ? ` · ${meta.categoryCounts[c] ?? 0}` : ""}
            </option>
          ))}
        </select>

        <select
          value={props.sort}
          onChange={(e) => props.onSort(e.target.value as MaterialSort)}
          aria-label="Сортування"
          className="rounded-lg border bg-card px-3 py-2 text-sm"
        >
          {(Object.keys(MATERIAL_SORT_LABEL) as MaterialSort[]).map((s) => (
            <option key={s} value={s}>{MATERIAL_SORT_LABEL[s]}</option>
          ))}
        </select>

        {canCreate && (
          <button
            type="button"
            onClick={props.onAdd}
            disabled={!props.onAdd}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60 md:w-auto"
          >
            <Plus className="h-4 w-4" aria-hidden /> Додати матеріал
          </button>
        )}
        {limitReached && (
          <button
            type="button"
            disabled
            aria-describedby="materials-limit-help"
            className="inline-flex cursor-not-allowed items-center justify-center gap-1.5 rounded-lg bg-muted px-3.5 py-2 text-sm font-semibold text-muted-foreground"
          >
            <Plus className="h-4 w-4" aria-hidden /> Додати матеріал
          </button>
        )}
      </div>
      {limitReached && (
        <p id="materials-limit-help" className="sr-only">
          Досягнуто ліміт матеріалів — створення заблоковане.
        </p>
      )}

      {/* Category chips (desktop) — backend counts, stable across search. */}
      <div role="group" aria-label="Фільтр за категорією" className="mb-3 hidden flex-wrap gap-1.5 md:flex">
        <CategoryChip
          label={`Усі · ${meta?.activeCount ?? 0}`}
          active={props.category === ""}
          onClick={() => props.onCategory("")}
        />
        {CATEGORY_KEYS.map((c) => (
          <CategoryChip
            key={c}
            label={`${MATERIAL_CATEGORY_LABEL[c]} · ${meta?.categoryCounts[c] ?? 0}`}
            active={props.category === c}
            onClick={() => props.onCategory(c)}
          />
        ))}
      </div>

      {/* List states */}
      {state.isPending ? (
        <div aria-hidden className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : state.isError ? (
        <div className="rounded-xl border border-dashed px-5 py-8 text-center">
          <p className="font-semibold">Не вдалося завантажити матеріали</p>
          <button
            type="button"
            onClick={state.refetch}
            className="mt-3 rounded-lg border px-3.5 py-2 text-sm font-medium hover:bg-accent"
          >
            Повторити
          </button>
        </div>
      ) : items.length === 0 ? (
        hasFilters ? (
          <div className="rounded-xl border border-dashed px-5 py-8 text-center">
            <p className="font-semibold">
              {props.category !== ""
                ? `У категорії «${MATERIAL_CATEGORY_LABEL[props.category]}» немає матеріалів`
                : "Нічого не знайдено"}
            </p>
            <button
              type="button"
              onClick={props.onResetFilters}
              className="mt-3 rounded-lg border px-3.5 py-2 text-sm font-medium hover:bg-accent"
            >
              Скинути фільтр
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed px-5 py-8 text-center">
            <p className="font-semibold">Матеріалів поки немає</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {canCreate
                ? "КП, узгоджені матриці та маркетингові матеріали для цього холдингу зберігаються тут."
                : "Ви маєте доступ лише для перегляду. Коли інший користувач додасть матеріали, вони зʼявляться тут."}
            </p>
            {canCreate && (
              <button
                type="button"
                onClick={props.onAdd}
                disabled={!props.onAdd}
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                <Plus className="h-4 w-4" aria-hidden /> Додати перший матеріал
              </button>
            )}
          </div>
        )
      ) : (
        <ul className="divide-y rounded-xl border bg-card">
          {items.map((m) => (
            <MaterialRow
              key={m.id}
              material={m}
              focused={props.focusId === m.id}
              onView={props.onView}
              onEdit={props.onEdit}
              onDelete={props.onDelete}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function CategoryChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active ? "border-primary bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
}

function MaterialRow({
  material: m,
  focused,
  onView,
  onEdit,
  onDelete,
}: {
  material: ClientMaterial;
  focused?: boolean;
  onView?: (m: ClientMaterial) => void;
  onEdit?: (m: ClientMaterial) => void;
  onDelete?: (m: ClientMaterial) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rowRef = useRef<HTMLLIElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focused) rowRef.current?.focus();
  }, [focused]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const date = useMemo(() => {
    const d = new Date(m.createdAt.replace(" ", "T") + "Z");
    return Number.isNaN(d.getTime()) ? m.createdAt : d.toLocaleDateString("uk-UA");
  }, [m.createdAt]);

  const doDownload = () => {
    setBusy(true);
    void downloadAttachment(materialDownloadUrl(m.id), m.originalFilename)
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };

  // ⋯ рендериться ЛИШЕ коли backend дав write-дії (TZ §3.2: порожній
  // overflow-набір ховається; read-only бачить view/download, а «нова
  // вкладка» доступна з хедера viewer-а).
  const overflow: Array<{ key: string; label: string; danger?: boolean; run: () => void }> = [];
  const hasWriteActions = (m.allowedActions.editMeta && !!onEdit) || (m.allowedActions.delete && !!onDelete);
  if (m.allowedActions.view && hasWriteActions) {
    overflow.push({
      key: "newtab",
      label: "Відкрити в новій вкладці",
      // Попап-блок (window.open поза transient activation) → чесний фолбек
      // на download, не мовчазне «нічого не сталося» (D-146 W2 fix).
      run: () =>
        void openAttachmentInNewTab(materialViewUrl(m.id))
          .then((opened) =>
            opened ? undefined : downloadAttachment(materialDownloadUrl(m.id), m.originalFilename),
          )
          .catch(() => undefined),
    });
  }
  if (m.allowedActions.editMeta && onEdit) {
    overflow.push({ key: "edit", label: "Змінити назву / категорію", run: () => onEdit(m) });
  }
  if (m.allowedActions.delete && onDelete) {
    overflow.push({ key: "delete", label: "Видалити", danger: true, run: () => onDelete(m) });
  }

  return (
    <li
      ref={rowRef}
      tabIndex={-1}
      className={cn(
        "relative flex items-center gap-3 px-3 py-2.5 outline-none sm:px-4",
        focused && "ring-2 ring-primary/50",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[10px] font-bold uppercase text-white",
          EXT_TILE[m.extension] ?? "bg-slate-500",
        )}
      >
        {m.extension ? m.extension.toUpperCase() : <FileText className="h-4 w-4" />}
      </span>

      <div className="min-w-0 flex-1">
        <p
          className="line-clamp-2 text-sm font-medium md:truncate"
          title={`Оригінальний файл: ${m.originalFilename}`}
        >
          {m.name}
        </p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span className="rounded bg-muted px-1.5 py-px font-semibold text-foreground/70">
            {MATERIAL_CATEGORY_LABEL[m.category]}
          </span>
          <span className="uppercase">{m.extension}</span>
          <span>{formatFileSize(m.fileSize)}</span>
          <span>{date}</span>
          {m.createdBy.name && <span>{m.createdBy.name}</span>}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {m.allowedActions.view && onView && (
          <button
            type="button"
            aria-label={`Переглянути «${m.name}»`}
            title="Переглянути"
            onClick={() => onView(m)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <Eye className="h-4 w-4" />
          </button>
        )}
        {m.allowedActions.download && (
          <button
            type="button"
            aria-label={`Завантажити «${m.name}»`}
            title="Завантажити"
            disabled={busy}
            onClick={doDownload}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          </button>
        )}
        {/* Порожній overflow-набір → ⋯ НЕ рендериться (TZ §3.2). */}
        {overflow.length > 0 && (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              aria-label="Інші дії"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <MoreVertical className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-9 z-20 min-w-[220px] rounded-lg border bg-card p-1 shadow-lg"
              >
                {overflow.map((a) => (
                  <button
                    key={a.key}
                    role="menuitem"
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      a.run();
                    }}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm hover:bg-muted",
                      a.danger && "text-destructive",
                    )}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}
